import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, Trash2, Save, GripVertical } from 'lucide-react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/apiClient';

interface SequenceStep {
  id: string;
  stepOrder: number;
  channel: string;
  delayMinutes: number;
  payload: Record<string, unknown>;
  isBranch: boolean;
  branchCondition: Record<string, unknown> | null;
}

interface SequenceDetail {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  steps: SequenceStep[];
  createdAt: string;
  updatedAt: string;
}

interface DraftStep {
  channel: string;
  delayMinutes: number;
  payloadText: string;
  isBranch: boolean;
  branchConditionText: string;
}

const CHANNELS = ['EMAIL', 'SMS', 'WHATSAPP', 'VOICE', 'TASK', 'LINKEDIN_MESSAGE', 'LINKEDIN_CONNECTION'] as const;

function blankDraftStep(): DraftStep {
  return {
    channel: 'EMAIL',
    delayMinutes: 0,
    payloadText: '{}',
    isBranch: false,
    branchConditionText: '',
  };
}

function stepToPayloadPlaceholder(channel: string): string {
  switch (channel) {
    case 'EMAIL': return JSON.stringify({ toAddress: '', fromAddress: '', subject: '', body: '' }, null, 2);
    case 'SMS': return JSON.stringify({ toNumber: '', fromNumber: '', body: '' }, null, 2);
    case 'WHATSAPP': return JSON.stringify({ toNumber: '', fromNumber: '', body: '' }, null, 2);
    case 'VOICE': return JSON.stringify({ toNumber: '', fromNumber: '', scriptUrl: '' }, null, 2);
    case 'TASK': return JSON.stringify({ title: '' }, null, 2);
    case 'LINKEDIN_MESSAGE': return JSON.stringify({ profileUrl: '', message: '' }, null, 2);
    case 'LINKEDIN_CONNECTION': return JSON.stringify({ profileUrl: '', note: '' }, null, 2);
    default: return '{}';
  }
}

export function SequenceEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [sequence, setSequence] = useState<SequenceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState<DraftStep[]>([]);
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    apiClient.get<SequenceDetail>(`/api/v1/sequences/${id}`)
      .then((res) => {
        const seq = res.data;
        setSequence(seq);
        setName(seq.name);
        setIsActive(seq.isActive);
        setSteps(seq.steps.map((s) => ({
          channel: s.channel,
          delayMinutes: s.delayMinutes,
          payloadText: JSON.stringify(s.payload, null, 2),
          isBranch: s.isBranch,
          branchConditionText: s.branchCondition ? JSON.stringify(s.branchCondition, null, 2) : '',
        })));
      })
      .catch(() => setError(t('sequences.not_found')))
      .finally(() => setLoading(false));
  }, [id, t]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleAddStep = () => {
    setSteps([...steps, blankDraftStep()]);
  };

  const handleRemoveStep = (idx: number) => {
    setSteps(steps.filter((_, i) => i !== idx));
  };

  const handleStepChange = (idx: number, field: keyof DraftStep, value: unknown) => {
    const updated = [...steps];
    updated[idx] = { ...updated[idx], [field]: value };
    if (field === 'channel') {
      updated[idx].payloadText = stepToPayloadPlaceholder(value as string);
    }
    setSteps(updated);
  };

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await apiClient.patch(`/api/v1/sequences/${id}`, { name, isActive });
      const parsedSteps = steps.map((s) => {
        let payload: Record<string, unknown> = {};
        let branchCondition: Record<string, unknown> | null = null;
        try { payload = JSON.parse(s.payloadText) as Record<string, unknown>; } catch { /* keep empty */ }
        if (s.isBranch && s.branchConditionText) {
          try { branchCondition = JSON.parse(s.branchConditionText) as Record<string, unknown>; } catch { /* keep null */ }
        }
        return {
          channel: s.channel,
          delayMinutes: s.delayMinutes,
          payload,
          isBranch: s.isBranch,
          branchCondition,
        };
      });
      await apiClient.put(`/api/v1/sequences/${id}/steps`, parsedSteps);
      showToast(t('sequences.saved'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="p-6 text-muted-foreground">{t('common.loading')}</p>;
  if (error || !sequence) return <p className="p-6 text-destructive">{error || t('sequences.not_found')}</p>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <Breadcrumbs crumbs={[
        { label: t('sequences.title'), to: '/admin/sequences' },
        { label: sequence.name },
      ]} />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/sequences">
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('sequences.back')}
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">{t('sequences.editor.title')}</h1>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {t('common.save')}
        </Button>
      </div>

      {toast && (
        <div className="bg-green-900/40 text-green-200 px-4 py-2 rounded text-sm">{toast}</div>
      )}

      <div className="space-y-3">
        <label className="block text-sm font-medium">{t('sequences.table.name')}</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          {t('sequences.table.active')}
        </label>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">{t('sequences.editor.steps_title')}</h2>
          <Button size="sm" onClick={handleAddStep}>
            <Plus className="w-4 h-4 mr-1" />
            {t('sequences.editor.add_step')}
          </Button>
        </div>

        {steps.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('sequences.empty')}</p>
        )}

        {steps.map((step, idx) => (
          <div key={idx} className="border rounded-lg p-4 space-y-3 bg-muted/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-semibold">
                  {t('sequences.editor.step', { n: idx + 1 })}
                </span>
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleRemoveStep(idx)}>
                <Trash2 className="w-3.5 h-3.5 text-destructive" />
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">{t('sequences.editor.channel')}</label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={step.channel}
                  onChange={(e) => handleStepChange(idx, 'channel', e.target.value)}
                >
                  {CHANNELS.map((c) => (
                    <option key={c} value={c}>{t(`sequences.channel.${c}`)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">{t('sequences.editor.delay')}</label>
                <Input
                  type="number"
                  min={0}
                  value={step.delayMinutes}
                  onChange={(e) => handleStepChange(idx, 'delayMinutes', Number(e.target.value))}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">{t('sequences.editor.payload')}</label>
              <textarea
                className="w-full border rounded px-3 py-2 text-xs font-mono min-h-[100px]"
                value={step.payloadText}
                onChange={(e) => handleStepChange(idx, 'payloadText', e.target.value)}
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={step.isBranch}
                onChange={(e) => handleStepChange(idx, 'isBranch', e.target.checked)}
              />
              {t('sequences.editor.branch')}
            </label>

            {step.isBranch && (
              <div>
                <label className="block text-xs font-medium mb-1">{t('sequences.editor.branch_condition')}</label>
                <textarea
                  className="w-full border rounded px-3 py-2 text-xs font-mono min-h-[60px]"
                  value={step.branchConditionText}
                  onChange={(e) => handleStepChange(idx, 'branchConditionText', e.target.value)}
                  placeholder='{"event":"REPLY"}'
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
